import type { GtmBrain } from "../api/GtmApi";

export type VoiceDocId = "tone" | "linkedin" | "blog";

export type VoiceDoc = {
  id: VoiceDocId;
  label: string;
  description: string;
  icon: "tone" | "linkedin" | "blog";
  filled: number;
  total: number;
};

export type VoiceToneFields = {
  archetype: string;
  description: string;
  guidelines: string;
  preferredLanguage: string;
  avoidedLanguage: string;
  bannedClaims: string;
};

export type VoiceChannelFields = {
  guidance: string;
  examples: string;
};

function hasText(value?: string | null): boolean {
  return !!value && value.trim().length > 0;
}

function filledCount(checks: boolean[]): { filled: number; total: number } {
  return { filled: checks.filter(Boolean).length, total: checks.length };
}

/** Parse toneOfVoice: structured JSON or plain text → description. */
export function parseVoiceTone(raw?: string | null): VoiceToneFields {
  const empty: VoiceToneFields = {
    archetype: "",
    description: "",
    guidelines: "",
    preferredLanguage: "",
    avoidedLanguage: "",
    bannedClaims: "",
  };
  const text = (raw || "").trim();
  if (!text) return empty;
  try {
    const parsed = JSON.parse(text) as Record<string, unknown>;
    if (parsed && typeof parsed === "object" && !Array.isArray(parsed)) {
      const looksStructured =
        "archetype" in parsed ||
        "description" in parsed ||
        "guidelines" in parsed ||
        "preferredLanguage" in parsed ||
        "avoidedLanguage" in parsed ||
        "bannedClaims" in parsed;
      if (looksStructured) {
        return {
          archetype: String(parsed.archetype ?? ""),
          description: String(parsed.description ?? ""),
          guidelines: Array.isArray(parsed.guidelines)
            ? (parsed.guidelines as string[]).join("\n")
            : String(parsed.guidelines ?? ""),
          preferredLanguage: String(parsed.preferredLanguage ?? ""),
          avoidedLanguage: String(parsed.avoidedLanguage ?? ""),
          bannedClaims: String(parsed.bannedClaims ?? ""),
        };
      }
    }
  } catch {
    /* plain text */
  }
  return { ...empty, description: text };
}

export function serializeVoiceTone(fields: VoiceToneFields): string {
  const hasAny = Object.values(fields).some((v) => hasText(v));
  if (!hasAny) return "";
  const onlyDescription =
    hasText(fields.description) &&
    !hasText(fields.archetype) &&
    !hasText(fields.guidelines) &&
    !hasText(fields.preferredLanguage) &&
    !hasText(fields.avoidedLanguage) &&
    !hasText(fields.bannedClaims);
  if (onlyDescription) return fields.description.trim();
  return JSON.stringify({
    archetype: fields.archetype.trim(),
    description: fields.description.trim(),
    guidelines: fields.guidelines
      .split("\n")
      .map((l) => l.replace(/^[-•*]\s*/, "").trim())
      .filter(Boolean),
    preferredLanguage: fields.preferredLanguage.trim(),
    avoidedLanguage: fields.avoidedLanguage.trim(),
    bannedClaims: fields.bannedClaims.trim(),
  });
}

export function parseVoiceChannel(raw?: string | null): VoiceChannelFields {
  const text = (raw || "").trim();
  if (!text) return { guidance: "", examples: "" };
  try {
    const parsed = JSON.parse(text) as Record<string, unknown>;
    if (parsed && typeof parsed === "object" && !Array.isArray(parsed)) {
      if ("guidance" in parsed || "examples" in parsed) {
        return {
          guidance: String(parsed.guidance ?? ""),
          examples: String(parsed.examples ?? ""),
        };
      }
    }
  } catch {
    /* plain text */
  }
  return { guidance: text, examples: "" };
}

export function serializeVoiceChannel(fields: VoiceChannelFields): string {
  const hasAny = hasText(fields.guidance) || hasText(fields.examples);
  if (!hasAny) return "";
  if (hasText(fields.guidance) && !hasText(fields.examples)) return fields.guidance.trim();
  return JSON.stringify({
    guidance: fields.guidance.trim(),
    examples: fields.examples.trim(),
  });
}

export function voiceToneSections(brain: GtmBrain): Array<{ id: string; label: string; body: string }> {
  const t = parseVoiceTone(brain.toneOfVoice);
  return [
    { id: "archetype", label: "Archetype", body: t.archetype },
    { id: "description", label: "Description", body: t.description },
    { id: "guidelines", label: "Guidelines", body: t.guidelines },
    { id: "preferredLanguage", label: "Preferred language", body: t.preferredLanguage },
    { id: "avoidedLanguage", label: "Avoided language", body: t.avoidedLanguage },
    { id: "bannedClaims", label: "Banned claims", body: t.bannedClaims },
  ];
}

export function voiceChannelSections(
  brain: GtmBrain,
  channel: "linkedin" | "blog"
): Array<{ id: string; label: string; body: string }> {
  const raw = channel === "linkedin" ? brain.voiceLinkedin : brain.voiceBlog;
  const c = parseVoiceChannel(raw);
  return [
    { id: "guidance", label: "Guidance", body: c.guidance },
    { id: "examples", label: "Examples", body: c.examples },
  ];
}

export function voiceDocs(brain: GtmBrain): VoiceDoc[] {
  const tone = parseVoiceTone(brain.toneOfVoice);
  const toneFill = filledCount([
    hasText(tone.archetype),
    hasText(tone.description),
    hasText(tone.guidelines),
    hasText(tone.preferredLanguage),
    hasText(tone.avoidedLanguage),
    hasText(tone.bannedClaims),
  ]);
  const li = parseVoiceChannel(brain.voiceLinkedin);
  const liFill = filledCount([hasText(li.guidance), hasText(li.examples)]);
  const blog = parseVoiceChannel(brain.voiceBlog);
  const blogFill = filledCount([hasText(blog.guidance), hasText(blog.examples)]);

  return [
    {
      id: "tone",
      label: "Tone of voice",
      description: "How you sound, across everything I draft for you.",
      icon: "tone",
      filled: toneFill.filled,
      total: toneFill.total,
    },
    {
      id: "linkedin",
      label: "LinkedIn posts",
      description: "How LinkedIn should read.",
      icon: "linkedin",
      filled: liFill.filled,
      total: liFill.total,
    },
    {
      id: "blog",
      label: "Blog articles",
      description: "How long-form should read.",
      icon: "blog",
      filled: blogFill.filled,
      total: blogFill.total,
    },
  ];
}

export const VOICE_SUBNAV = [
  { id: "tone" as const, label: "Tone of voice" },
  { id: "linkedin" as const, label: "LinkedIn posts" },
  { id: "blog" as const, label: "Blog articles" },
];
